import React, { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

// Context
import { CoreContext } from "../../context";

// Hooks
import {
  useSignatureCanvas,
  useTimesheetSubmission,
  useManagerShiftManagement,
} from "../../hooks/timekeeping";

// Features
import Title from "../../features/timekeeping/superTimesheet/Title";
import SuperShifts from "../../features/timekeeping/superTimesheet/SuperShifts";
import Checklist from "../../features/timekeeping/superTimesheet/Checklist";
import { Button } from "../../components/ui";
import { ModalWrapper } from "../../components/ui";
import { Spinner } from "../../components/ui";

// Components
import useSuperTimesheetCtx, {
  SuperTimesheetContext,
} from "../../context/page/SuperTimesheetContext";
import { BasicCard } from "../../components/ui";

// Utils
import { debounce } from "../../utils";

// API
import { saveManagerTimesheet } from "../../services/api/timekeeping";

/**
 * SuperTimesheet component renders a styled container with customizable classes and text.
 *
 * Please do not import this component directly. Instead, use it as a template/example
 * for creating new components.
 *
 * @param {Object} props - The properties for the component.
 * * @param {string} props.className - Additional CSS classes for custom styling.
 * * @param {string} props.text - The text content to display inside the component.
 */
const SuperTimesheet = () => {
  const { userId, weekEnding: urlWeekEnding } = useParams();
  const superTimesheetCtx = useSuperTimesheetCtx(
    userId ? parseInt(userId) : null,
    urlWeekEnding
  );
  const navigate = useNavigate();
  const { appName } = useContext(CoreContext);
  const {
    combinedWeeklyHours,
    setCombinedWeeklyHours,
    hoursConflicts,
    managerComments,
    weekEnding,
    workerSignature,
    worker,
  } = superTimesheetCtx;
  // Start as editing if there are initial conflicts
  const [isEditing, setIsEditing] = useState(hoursConflicts);
  const [isSubmittable, setIsSubmittable] = useState(false);

  // Shift management hook
  const { handleShiftsUpdate, addShiftToDay, removeShiftFromDay } =
    useManagerShiftManagement(setCombinedWeeklyHours);

  // Signature canvas hook
  const {
    signatureRef: managerSignatureRef,
    signatureContainerRef,
    isSigned: isManagerSigned,
    clearSignature,
    getSignatureData,
    validateSignature,
    signatureCanvasOptions,
    signatureCanvasCanvasProps,
    onSignatureEnd,
  } = useSignatureCanvas();

  // Timesheet submission hook
  const {
    isSubmitting,
    showSuccessModal,
    handleSubmit: handleSubmitTimesheetBase,
    closeSuccessModal,
  } = useTimesheetSubmission({
    submitFunction: saveManagerTimesheet,
    onSuccess: () => {},
    onError: (error) => {
      alert(error);
    },
  });

  // Debounced function to update manager comments
  const debouncedUpdateComments = useMemo(
    () =>
      debounce((comments) => {
        managerComments.current = comments;
      }, 300), // 300ms delay for good UX
    [managerComments]
  );

  // Handle manager comments change
  const handleCommentsChange = (e) => {
    const comments = e.target.value;
    debouncedUpdateComments(comments);
  };

  const handleSuccessModalClose = () => {
    closeSuccessModal();
    navigate("/timekeeping/timesheets");
  };

  // Calculate unique job numbers from all shifts
  const uniqueJobNumbers = useMemo(() => {
    const jobSet = new Set();
    combinedWeeklyHours.forEach((day) => {
      day.shifts?.forEach((shift) => {
        // Use jobPayout if it exists (manager override), otherwise use job
        const jobId = shift.jobPayout || shift.job;
        if (jobId) {
          jobSet.add(jobId);
        }
      });
    });
    return Array.from(jobSet);
  }, [combinedWeeklyHours]);

  const maxJobsExceeded = uniqueJobNumbers.length > 5;

  // Update submittable state based on conditions
  useEffect(() => {
    // Check if all requirements are met to enable submission
    if (isManagerSigned && !isEditing && !isSubmitting && !maxJobsExceeded) {
      setIsSubmittable(true);
    } else {
      setIsSubmittable(false);
    }
  }, [isManagerSigned, isEditing, isSubmitting, maxJobsExceeded]);

  // Submit timesheet function reads from the context which is updated by the shifts
  // component when user edits their shifts.
  const handleSubmitTimesheet = () => {
    // Validate signature
    const validation = validateSignature();
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Get signature data
    const signatureData = getSignatureData();

    const dataToSubmit = {
      worker: worker,
      weekEnding: weekEnding,
      payoutData: combinedWeeklyHours,
      signature: signatureData,
      managerComments: managerComments.current || "",
    };

    handleSubmitTimesheetBase(dataToSubmit);
  };

  if (!worker) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="h-12 w-12" className="text-green-900" />
      </div>
    );
  }

  document.title = `Payout: ${worker.first} ${worker.last} - ${weekEnding}`;
  appName.current = "SuperTimesheet";

  return (
    <SuperTimesheetContext.Provider value={superTimesheetCtx}>
      <div className="max-w-[1024px] min-w-[640px]  mx-auto px-2 md:px-4 mb-96 font-inter caret-transparent bg-gray-100 flex flex-col items-center justify-center p-4">
        {/* Back Button */}
        <div className="w-full mb-4">
          <button
            onClick={() => navigate("/timekeeping/timesheets")}
            className="flex text-sm font-semibold items-center text-green-900 hover:text-green-800 transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Timesheet List
          </button>
        </div>

        <Title worker={worker} className={"mb-2"} />
        <SuperShifts
          worker={worker}
          weekEnding={weekEnding}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          shiftsToRender={combinedWeeklyHours}
          setShiftsToRender={handleShiftsUpdate}
          onAddShift={addShiftToDay}
          onRemoveShift={removeShiftFromDay}
          className={"mb-2"}
        />

        {/* Manager Comments Section */}
        <div className="w-full mb-2 p-2 py-1 bg-gray-200 rounded-md">
          <label
            htmlFor="manager-comments"
            className="block text-sm font-semibold text-gray-700"
          >
            Manager Comments
          </label>
          <textarea
            id="manager-comments"
            rows={2}
            className="w-full caret-black text-sm px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
            placeholder="Add any comments about this timesheet..."
            onChange={handleCommentsChange}
            defaultValue={managerComments.current || ""}
          />
        </div>

        <Checklist
          // TODO: Update backend to send timesheet and hourslog load confirmations as top level vars
          workerTimesheet={combinedWeeklyHours.length > 0}
          hoursLog={combinedWeeklyHours.length > 0}
          hasConflicts={superTimesheetCtx.hoursConflicts.current}
          missingLogs={superTimesheetCtx.missingLogs.current}
          isEditing={isEditing}
          isManagerSigned={isManagerSigned}
          isSubmittable={isSubmittable}
          setIsSubmittable={setIsSubmittable}
          maxJobsExceeded={maxJobsExceeded}
          className={"w-full mb-2 bg-gray-200 rounded-md min-h-[52px]"}
        />
        <BasicCard
          bg="bg-gray-200"
          text="I certify that I have reviewed this timesheet and believe the hours recorded are accurate and complete. I confirm that any reported overtime was necessary and that the employee’s recorded meal and rest breaks were taken or reported. I am not aware of any unreported work-related injuries or illnesses."
          className="font-bold text-sm w-full mb-3 p-2"
        />

        {/* Signature Section */}
        <div className="w-full mb-2 flex flex-col md:flex-row gap-4">
          {/* Worker Signature */}
          <div className="flex-1">
            <div className="border border-gray-300 rounded-t-lg overflow-hidden">
              <div className="border-b font-bold border-gray-300 bg-gray-200 p-2 text-center">
                Worker Signature
              </div>
              <div className="p-0 bg-white h-64 flex items-center justify-center">
                {workerSignature ? (
                  <img
                    src={workerSignature}
                    alt="Worker Signature"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-gray-500">No signature provided</span>
                )}
              </div>
            </div>
            {/* Button is for even heights on both signatures and only displays name */}
            <Button
              text={`${worker.first} ${worker.initial ? worker.initial + " " : ""}${worker.last}`}
              variant={"disabled"}
              className="flex-1 rounded-t-none"
            />
          </div>

          {/* Manager Signature */}
          <div className="flex-1">
            <div
              className="border border-gray-300 rounded-t-lg overflow-hidden"
              ref={signatureContainerRef}
            >
              <div className="border-b font-bold border-gray-300 bg-gray-200 p-2 text-center">
                Manager Signature
              </div>
              <div className="flex flex-col items-center bg-gray-100">
                <SignatureCanvas
                  ref={managerSignatureRef}
                  options={signatureCanvasOptions}
                  onEnd={onSignatureEnd}
                  canvasProps={signatureCanvasCanvasProps}
                />
              </div>
            </div>
            <Button
              text="Clear"
              variant={isManagerSigned ? "warning" : "disabled"}
              className="flex-1 rounded-t-none"
              onClick={clearSignature}
            />
          </div>
        </div>
        <Button
          text={
            isSubmitting
              ? ""
              : showSuccessModal
                ? "Submitted"
                : "Submit Timesheet"
          }
          icon={isSubmitting ? "spinner" : undefined}
          variant={
            showSuccessModal
              ? "disabled"
              : isSubmittable
                ? "primary"
                : "disabled"
          }
          className="mb-4 w-full"
          onClick={handleSubmitTimesheet}
        />
        <ModalWrapper
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
        >
          <div className="text-center p-4">
            <div className="mb-4">
              <div className="text-green-600 text-6xl mb-4">✓</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Timesheet Submitted Successfully!
              </h2>
              <p className="text-gray-600 mb-4">
                The timesheet has been successfully reviewed and approved.
              </p>
              <p className="text-gray-600 mb-6">
                You will be returned to the timesheet list when you press OK.
              </p>
            </div>
            <Button
              text="OK"
              variant="primary"
              className="w-full"
              onClick={handleSuccessModalClose}
            />
          </div>
        </ModalWrapper>
      </div>
    </SuperTimesheetContext.Provider>
  );
};

export default SuperTimesheet;
